import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { isEmpty } from 'lodash-es';

import useMeta from '~coreModules/core/js/seo/composables/use-meta';
import { useState } from '~coreModules/core/js/composables/vuexHelpers';
import { useLogger } from '~coreModules/core/js/composables/useLogger';
import { useUserState } from '~coreModules/core/js/composables/useUserState';
import { useRuntimeConfig } from '~coreModules/core/js/composables/useRuntimeConfig';

import { getRobots } from '~coreModules/core/js/seo/meta-utils';

import { BROWSE_MODULE_NAME } from '~routes/browse/js/browse-store';
import { NAVIGATION_MODULE_NAME } from '~modules/nav/js/nav-store';

export default function useBrowseMeta() {
    const { t } = useI18n();
    const route = useRoute();
    const logger = useLogger();
    const userState = useUserState();
    const runtimeConfig = useRuntimeConfig();
    const { defaultTitle } = runtimeConfig.siteMeta;

    const { flatNavigation } = useState(NAVIGATION_MODULE_NAME, [
        'flatNavigation',
    ]);
    const {
        products,
        brandName,
        contentfulCurationNavItems,
    } = useState(BROWSE_MODULE_NAME, {
        products: state => state.catalog.products,
        brandName: state => state.brandName,
        contentfulCurationNavItems: state => state.contentfulContent?.curationNavItems,
    });

    const navItem = computed(() => {
        const { slug } = route.params;

        if (!flatNavigation.value?.[slug]) {
            logger.debugError('Browse navigation slug does not exist for ', slug);
            return {};
        }

        return flatNavigation.value[slug];
    });
    const navItemDisplayName = computed(() => navItem.value?.displayName);
    const navItemSEO = computed(() => navItem.value?.seo);
    const browseUrl = computed(() => {
        const baseUrl = userState?.baseUrl || '';
        return `${baseUrl}${route.path}`;
    });
    const curationQuery = computed(() => route.query?.curation);
    const curationUrl = computed(() => (curationQuery.value ? `?curation=${curationQuery.value}` : ''));

    const productImage = computed(() => {
        if (isEmpty(products.value)) return '';

        const images = products.value?.[0]?.images;
        if (isEmpty(images)) return '';

        return images[0];
    });

    const metaDescription = computed(() => {
        const curationNavItem = contentfulCurationNavItems.value?.find?.(item => item.slug === curationQuery.value);
        if (curationNavItem) return curationNavItem?.metaDescription || '';

        return brandName.value ?
            t('meta__brand--description', [brandName.value]) : navItemSEO.value?.metaDescription;
    });

    const ogUrl = computed(() => {
        if (curationUrl.value) return `${browseUrl.value}${curationUrl.value}`;
        return browseUrl.value;
    });

    const robots = computed(() => {
        if (brandName.value) {
            return {
                name: 'robots',
                content: getRobots(false, false),
            };
        }

        if (isEmpty(navItem.value) || !navItemSEO.value) return {};
        const { noIndex, noFollow } = navItemSEO.value;

        return {
            name: 'robots',
            content: getRobots(noIndex, noFollow),
        };
    });

    const pageTitle = computed(() => {
        if (brandName.value) return t('meta__brand--title', [brandName.value]);

        const title = navItemSEO.value?.title;
        if (title) return t(defaultTitle, [title]);

        if (navItemDisplayName.value) return t(defaultTitle, [navItemDisplayName.value]);

        return null;
    });

    function getMetaTags() {
        if (!brandName.value && (isEmpty(navItem.value) || !navItemSEO.value)) return [];

        return [
            robots.value,
            {
                property: 'og:title',
                content: pageTitle.value,
            }, {
                property: 'og:type',
                content: 'product.group',
            }, {
                property: 'og:ttl',
                content: '604800',
            }, {
                property: 'og:description',
                content: metaDescription.value,
            }, {
                name: 'description',
                content: metaDescription.value,
            }, {
                property: 'og:url',
                content: ogUrl.value,
            }, {
                property: 'og:image',
                content: productImage.value,
            },
        ];
    }

    function getPageTitle() {
        return pageTitle.value;
    }

    function getLinkTags() {
        return [...(curationUrl.value ? [{
            rel: 'canonical',
            href: `${browseUrl.value}${curationUrl.value}`,
        }] : [])];
    }

    useMeta({ getMetaTags, getPageTitle, getLinkTags });
}
